// MIRRORED FILE: byte-identical in DispatchSystem/services/chat/ and
// wolfdriver-mobile/services/chat/. No imports. See MIRROR.md.
//
// chatLiveness — the pure decision behind "should we poll for what the live
// channel may have missed, and when?".
//
// A realtime subscription is not a guarantee. The socket can be suspended,
// dropped or silently wedged: the channel still says it is subscribed while no
// event has arrived for minutes. So a chat view keeps a tiny state machine on
// the side that watches three signals — channel status, live payloads, and
// whether the view is in the foreground at all — and answers one question:
// the timestamp of the next catch-up poll, or null for "do not poll".
//
// Rules:
//   • Not in the foreground          → never poll (nothing is being read).
//   • Subscribed and heard from      → never poll; the channel is doing its job.
//     within `silentAfterMs`
//   • Subscribed but silent longer   → poll, then measure silence from that
//     poll, so a quiet-but-healthy channel costs one request per silence
//     window rather than a steady stream.
//   • Not subscribed                 → poll on a backoff that starts at
//     `minBackoffMs` and doubles on every poll up to `maxBackoffMs` — a poll
//     that succeeded only proves the network works, not the channel, so a
//     dead socket costs one request per `maxBackoffMs` rather than one per
//     `minBackoffMs` forever. The backoff resets when the channel comes back
//     or the view returns to the foreground.
//
// A successful poll counts as "heard from": it is fresh data from the server,
// exactly what a live payload would have been.

export interface LivenessOptions {
  /** Silence tolerated on a subscribed channel before polling anyway. */
  silentAfterMs?: number;
  /** First delay between catch-up polls while the channel is not subscribed. */
  minBackoffMs?: number;
  /** Ceiling the failure backoff doubles up to. */
  maxBackoffMs?: number;
}

export interface Liveness {
  /** A live payload arrived. */
  onEvent(nowMs: number): void;
  /** The channel reported a status. */
  onStatus(status: 'SUBSCRIBED' | 'CLOSED' | 'CHANNEL_ERROR' | 'TIMED_OUT' | string, nowMs: number): void;
  /** The view moved into (true) or out of (false) the foreground. */
  onForeground(active: boolean, nowMs: number): void;
  /** When the next catch-up poll is due (<= nowMs means now), or null. */
  nextPollAt(nowMs: number): number | null;
  /** A catch-up poll finished: ok resets the backoff, failure doubles it. */
  onPolled(nowMs: number, ok: boolean): void;
}

const laterOf = (a: number | null, b: number | null): number | null => {
  if (a == null) return b;
  if (b == null) return a;
  return a > b ? a : b;
};

export function createLiveness(options: LivenessOptions = {}): Liveness {
  const silentAfterMs = options.silentAfterMs ?? 45000;
  const minBackoffMs = options.minBackoffMs ?? 5000;
  const maxBackoffMs = Math.max(options.maxBackoffMs ?? 30000, minBackoffMs);

  let subscribed = false;
  let foreground = true;
  // Last moment fresh data was seen: a payload, a SUBSCRIBED status, or a poll
  // that came back ok.
  let heardAt: number | null = null;
  // Last moment the channel said it was not live.
  let downAt: number | null = null;
  let polledAt: number | null = null;
  let backoffMs = minBackoffMs;

  return {
    onEvent(nowMs: number): void {
      heardAt = nowMs;
    },

    onStatus(status: string, nowMs: number): void {
      if (status === 'SUBSCRIBED') {
        subscribed = true;
        downAt = null;
        heardAt = nowMs;
        backoffMs = minBackoffMs;
        return;
      }
      subscribed = false;
      // Keep the FIRST moment of this outage: a channel that reports CLOSED
      // over and over must not push its own catch-up poll further away.
      if (downAt == null) downAt = nowMs;
    },

    onForeground(active: boolean, nowMs: number): void {
      if (active === foreground) return;
      foreground = active;
      // Coming back: whatever made the last poll fail (usually no network) is
      // most likely over, so start again at the short delay instead of serving
      // a reader at the far end of an old backoff.
      if (active) {
        backoffMs = minBackoffMs;
        if (!subscribed && downAt == null) downAt = nowMs;
      }
    },

    nextPollAt(nowMs: number): number | null {
      if (!foreground) return null;
      if (subscribed) {
        if (heardAt == null) return null;
        if (nowMs - heardAt < silentAfterMs) return null;
        const due = heardAt + silentAfterMs;
        const afterFailure = polledAt == null ? due : polledAt + backoffMs;
        return afterFailure > due ? afterFailure : due;
      }
      const base = laterOf(laterOf(downAt, heardAt), polledAt);
      if (base == null) return null;
      return base + backoffMs;
    },

    onPolled(nowMs: number, ok: boolean): void {
      polledAt = nowMs;
      if (ok) heardAt = nowMs;
      // A poll that worked proves the NETWORK is healthy — it says nothing
      // about the channel. So the gap widens while the channel is still down,
      // succeed or fail: a socket that simply cannot connect (a Wi-Fi that
      // blocks websockets, realtime down) must settle at one request per
      // `maxBackoffMs`, not hold the fallback at `minBackoffMs` forever. Only
      // the channel coming back (onStatus SUBSCRIBED) or a foreground resume
      // shortens it again.
      if (ok && subscribed) {
        backoffMs = minBackoffMs;
        return;
      }
      const doubled = backoffMs * 2;
      backoffMs = doubled > maxBackoffMs ? maxBackoffMs : doubled;
    },
  };
}
